import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { PageTrackingSite } from '../api';
import type { PageTheme } from '../model/pages';
import { useEditorApi } from './editorApi';
import { HINT, SelectField, Switch, TextAreaField, TEXT_BTN } from './InspectorKit';

function TrackingSwitch({ label, hint, checked, onChange }: { label: string; hint: string; checked: boolean; onChange: (value: boolean) => void }) {
  const id = useId();
  return <label htmlFor={id} className="flex min-h-11 min-w-0 cursor-pointer items-center gap-3">
    <span className="grid min-w-0 flex-1 gap-0.5"><span className="text-sm text-text">{label}</span><span id={`${id}-hint`} className={HINT}>{hint}</span></span>
    <Switch id={id} label={label} checked={checked} onChange={onChange} describedBy={`${id}-hint`} />
  </label>;
}

/** Mirrors the collector's exact-domain/subdomain rule, including www aliases. */
export function trackingHostAllowed(host: string, domains: string[]): boolean {
  try {
    const name = new URL(`https://${host}`).hostname.toLowerCase().replace(/^www\./, '');
    return domains.some(domain => name === domain || name.endsWith(`.${domain}`));
  } catch { return false; }
}

export function PageTracking({ theme, setTheme, host }: {
  theme: PageTheme;
  setTheme: (patch: Partial<PageTheme>) => void;
  host: string;
}) {
  const api = useEditorApi();
  const tracking = theme.analytics_tracking ?? {};
  const enabled = tracking.enabled === true;
  const [sites, setSites] = useState<PageTrackingSite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const request = useRef(0);
  const load = useCallback(async () => {
    const id = ++request.current;
    setLoading(true); setError('');
    try {
      const result = await api.listPageTrackingSites();
      if (id === request.current) setSites(result.sites);
    } catch {
      if (id === request.current) setError('Could not load tracking sites. Try again.');
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [api]);
  useEffect(() => {
    if (enabled) void load();
    return () => { request.current++; };
  }, [enabled, load]);

  const selected = sites.find(site => site.id === tracking.site_id);
  const customEnabled = theme.custom_scripts_enabled ?? Boolean(theme.head_html || theme.body_html);
  const setupUrl = `/tracking${selected ? `?client_id=${selected.client_id}` : ''}#tracking-script`;

  return (
    <div className="grid min-w-0 gap-4 max-sm:[&_select]:!min-h-11 max-sm:[&_select]:!text-base max-sm:[&_textarea]:!text-base" data-page-tracking>
      <p className={HINT}>Views, clicks and form leads are already included in this page’s Analytics panel.</p>
      <TrackingSwitch label="Automatically add tracking" checked={enabled}
        hint="Send visits and phone clicks to a client’s Website Analytics."
        onChange={value => setTheme({ analytics_tracking: { ...tracking, enabled: value } })} />
      {enabled && <div className="grid min-w-0 gap-3">
        <SelectField label="Client tracking site" value={tracking.site_id ? String(tracking.site_id) : ''}
          onChange={value => setTheme({ analytics_tracking: { ...tracking, site_id: value ? Number(value) : undefined } })}>
          <option value="">{loading ? 'Loading sites…' : 'Choose a client and site'}</option>
          {!!tracking.site_id && !selected && <option value={tracking.site_id}>{loading || error ? 'Saved tracking site' : 'Unavailable tracking site'}</option>}
          {sites.map(site => <option key={site.id} value={site.id} disabled={!site.active}>
            {site.client_name}: {site.label || site.domains[0] || `Site ${site.id}`}{site.active ? '' : ' (paused)'}
          </option>)}
        </SelectField>
        {error && <p role="alert" className="text-xs text-danger">{error}</p>}
        {!loading && !error && !selected && <p className={HINT} role="status">
          {tracking.site_id ? 'This site is unavailable. Choose another site before publishing.' : sites.some(site => site.active) ? 'Choose where this page’s visits should appear before publishing.' : 'Create an active tracking site for the client, then refresh this list.'}
        </p>}
        {selected && <div className="grid min-w-0 gap-2">
          <p className={`${HINT} break-words`}>Allowed domains: {selected.domains.join(', ')}</p>
          {!selected.active ? <p role="status" className="text-xs text-danger">This tracking site is paused. Enable it in tracking setup or choose another site.</p>
            : host && !trackingHostAllowed(host, selected.domains)
              ? <p role="status" className={`${HINT} break-words`}>Tracking will not run on {host}. Add that domain in tracking setup, or share the page on an allowed domain.</p>
              : <p className={HINT}>The script is added automatically to the published page. No code to paste.</p>}
          <a href={`/website?client_id=${selected.client_id}&site=${selected.id}`} target="_blank" rel="noopener noreferrer"
            className="text-xs text-primary underline underline-offset-2 max-sm:inline-flex max-sm:min-h-11 max-sm:items-center">Open Website Analytics</a>
        </div>}
        <div className="flex flex-wrap items-center gap-2">
          <a href={setupUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline underline-offset-2 max-sm:inline-flex max-sm:min-h-11 max-sm:items-center">Tracking setup</a>
          <button type="button" className={`${TEXT_BTN} max-sm:!min-h-11`} disabled={loading} onClick={() => { void load(); }}>{loading ? 'Loading…' : 'Refresh sites'}</button>
        </div>
        <p className={HINT}>Previews never send tracking events. Publish changes to apply this setting.</p>
      </div>}

      <div className="grid min-w-0 gap-3 border-t border-border pt-4">
        <TrackingSwitch label="Run custom scripts" checked={customEnabled}
          hint="Google Analytics, Meta Pixel or another script you provide."
          onChange={value => setTheme({ custom_scripts_enabled: value })} />
        {customEnabled ? <>
          <p className={HINT}>Custom scripts run on published custom domains only. They stay off in the editor, previews and the app’s shared page links.</p>
          <p className={HINT}>Custom JavaScript is supported. Wrap it in &lt;script&gt; tags and add it to each page where it should run.</p>
          <TextAreaField mono rows={4} label="Header scripts, added to the live page’s <head>" value={theme.head_html ?? ''}
            onChange={value => setTheme({ head_html: value })} placeholder="<!-- Meta Pixel, Google Analytics, site verification meta... -->" />
          <TextAreaField mono rows={3} label="Body scripts, added before </body>" value={theme.body_html ?? ''}
            onChange={value => setTheme({ body_html: value })} placeholder="<!-- third-party widgets, end-of-body scripts... -->" />
        </> : <p className={HINT}>Scripts are off. Saved code is kept so you can turn it back on.</p>}
      </div>
    </div>
  );
}
